import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Avatar,
  Grid,
  Card,
  CardContent,
  Tooltip,

} from '@mui/material'
import EmployeeTableSkeleton from '../../components/common/Skeleton/EmployeeTableSkeleton'
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Face as FaceIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
  Visibility as VisibilityIcon,
  Upload as UploadIcon,
  Business as BusinessIcon,
  Refresh as RefreshIcon,
  People as PeopleIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { usePagination, useLoading, useTableFilters } from '../../hooks'
import {
  StatusChip,
  WorkShiftChip,
  EmployeeTypeChip,
  EnrollmentChip
} from '../../components/common/Chips'
import { exportToCSV } from '../../utils/exportUtils'
import { employeeApi } from '../../api/employeeApi'
import { shiftApi } from '../../api/shiftApi'
import { formatDate } from '../../utils/formatters'

const Employees = () => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const pagination = usePagination(10)
  const { loading, withLoading } = useLoading()
  const [employees, setEmployees] = useState([])
  const [shiftsExist, setShiftsExist] = useState(true) // optimistic default
  const { filters, updateFilter } = useTableFilters({
    department: 'all',
    status: 'all',
    enrollment: 'all'
  })

  const fetchEmployees = async () => {
    withLoading(async () => {
      try {
        // Fetch all employees for client-side filtering/pagination
        const response = await employeeApi.listEmployees()

        // Handle response format variations
        const employeeData = response.data.employees || response.data || []

        // Map snake_case to camelCase
        const mappedEmployees = employeeData.map(emp => ({
          id: emp.employee_id || emp.id,
          employeeId: emp.employee_code || emp.employeeId,
          firstName: emp.first_name || emp.firstName,
          lastName: emp.last_name || emp.lastName,
          email: emp.email,
          department: emp.department,
          position: emp.designation || emp.position,
          status: emp.status || 'active',
          faceEnrolled: emp.faceEnrolled || false,
          phone: emp.phone,
          joinDate: emp.join_date || emp.joinDate,
          workShift: emp.work_shift || emp.workShift,
          workHours: emp.work_hours || emp.workHours,
          workingDays: emp.working_days || emp.workingDays,
          employeeType: emp.employee_type || emp.employeeType,
          salary: emp.salary,
          address: emp.address,
          emergencyContact: emp.emergency_contact || emp.emergencyContact,
          emergencyPhone: emp.emergency_phone || emp.emergencyPhone,
          gender: emp.gender || '',
          photo: emp.photo,
        }))

        setEmployees(mappedEmployees)
      } catch (error) {
        console.error('Error fetching employees:', error)
        toast.error('Failed to fetch employees')
      }
    })
  }

  const fetchShiftsCheck = async () => {
    try {
      const response = await shiftApi.listShifts()
      const shiftList = response.data.shifts || []
      setShiftsExist(shiftList.length > 0)
    } catch (error) {
      console.error('Error checking shifts:', error)
    }
  }

  useEffect(() => {
    fetchEmployees()
    fetchShiftsCheck()
  }, [])

  const handleRefresh = () => {
    fetchEmployees()
    toast.success('Refreshed successfully!')
  }

  const handleAddEmployee = () => {
    if (!shiftsExist) {
      toast.error('No shifts found. Please create a shift first before adding an employee.')
      navigate('/org/shifts')
      return
    }
    navigate('/org/employees/add')
  }

  const handleEditEmployee = (employee) => {
    navigate(`/org/employees/${employee.id}/edit`)
  }

  const handleViewEmployee = (id) => {
    navigate(`/org/employees/${id}`)
  }

  const handleEnrollFace = (id) => {
    navigate(`/org/employees/${id}/enroll`)
  }

  const handleDeleteEmployee = async (employee) => {
    if (window.confirm(`Delete employee ${employee.firstName} ${employee.lastName}?`)) {
      try {
        await employeeApi.deleteEmployee(employee.id)
        fetchEmployees()
        toast.success('Employee deleted successfully!')
      } catch (error) {
        toast.error('Failed to delete employee')
      }
    }
  }

  const filteredEmployees = employees.filter(emp => {
    const fullName = `${emp.firstName} ${emp.lastName}`
    const matchesSearch = fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (emp.employeeId && emp.employeeId.toLowerCase().includes(searchTerm.toLowerCase()))

    const matchesDept = filters.department === 'all' || emp.department === filters.department
    const matchesStatus = filters.status === 'all' || emp.status === filters.status
    const matchesEnrollment = filters.enrollment === 'all' ||
      (filters.enrollment === 'enrolled' && emp.faceEnrolled) ||
      (filters.enrollment === 'not-enrolled' && !emp.faceEnrolled)

    return matchesSearch && matchesDept && matchesStatus && matchesEnrollment
  })

  const paginatedEmployees = filteredEmployees.slice(
    pagination.page * pagination.rowsPerPage,
    pagination.page * pagination.rowsPerPage + pagination.rowsPerPage
  )

  const stats = {
    totalEmployees: employees.length,
    activeEmployees: employees.filter(e => e.status === 'active').length,
    enrolledEmployees: employees.filter(e => e.faceEnrolled).length,
    departments: [...new Set(employees.map(e => e.department))].filter(Boolean).length,
    onRollEmployees: employees.filter(e => e.employeeType === 'onroll').length,
    outsourceEmployees: employees.filter(e => e.employeeType === 'outsource').length,
  }

  const handleExport = () => {
    const columns = [
      { label: 'Employee ID', key: 'employeeId' },
      { label: 'First Name', key: 'firstName' },
      { label: 'Last Name', key: 'lastName' },
      { label: 'Email', key: 'email' },
      { label: 'Department', key: 'department' },
      { label: 'Position', key: 'position' },
      { label: 'Status', key: 'status' },
      { label: 'Shift', key: 'workShift' },
      { label: 'Type', key: 'employeeType' },
      { label: 'Phone', key: 'phone' },
      { label: 'Join Date', key: (row) => formatDate(row.joinDate) }
    ]
    exportToCSV(filteredEmployees, columns, 'employees-list')
  }

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, bgcolor: 'background.paper', boxShadow: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 2, gap: 2 }}>
            <Typography variant="h4" fontWeight="bold">
              Employees Management
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
              <Button
                variant="outlined"
                onClick={handleRefresh}
                disabled={loading}
                startIcon={<RefreshIcon />}
                size="small"
                sx={{ flex: { xs: 1, sm: 'none' } }}
              >
                Refresh
              </Button>
            </Box>
          </Box>



        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#1976d2', mr: 2 }}>
                    <PeopleIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.totalEmployees}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total Employees
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#2e7d32', mr: 2 }}>
                    <CheckCircleIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.activeEmployees}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Active
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#9c27b0', mr: 2 }}>
                    <FaceIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.enrolledEmployees}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Face Enrolled
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#ed6c02', mr: 2 }}>
                    <BusinessIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.departments}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Departments
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#9c27b0', mr: 2 }}>
                    <BusinessIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.onRollEmployees}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      On Roll
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#6b23a7', mr: 2 }}>
                    <BusinessIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stats.outsourceEmployees}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Out Source
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters and Search */}
        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                placeholder="Search employees..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Filter selects unchanged */}
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Department</InputLabel>
                <Select
                  label="Department"
                  value={filters?.department || 'all'}
                  onChange={(e) => updateFilter('department', e.target.value)}
                >
                  <MenuItem value="all">All Departments</MenuItem>
                  <MenuItem value="HR">HR</MenuItem>
                  <MenuItem value="IT">IT</MenuItem>
                  <MenuItem value="Sales">Sales</MenuItem>
                  <MenuItem value="Production">Production</MenuItem>
                  <MenuItem value="Finance">Finance</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={filters?.status || 'all'}
                  onChange={(e) => updateFilter('status', e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="on-leave">On Leave</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Face Enrollment</InputLabel>
                <Select
                  label="Face Enrollment"
                  value={filters?.enrollment || 'all'}
                  onChange={(e) => updateFilter('enrollment', e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="enrolled">Enrolled</MenuItem>
                  <MenuItem value="not-enrolled">Not Enrolled</MenuItem>
                </Select>
              </FormControl>
            </Grid>


          </Grid>
        </Paper>


        {/* Employees Table */}
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ p: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'flex-end', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => {
                  setSearchTerm('')
                  updateFilter('department', 'all')
                  updateFilter('status', 'all')
                  updateFilter('enrollment', 'all')
                }}
                sx={{ flex: 1 }}
              >
                Clear
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExport}
                sx={{ flex: 1 }}
              >
                Export
              </Button>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddEmployee}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Add
            </Button>
          </Box>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 900, tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '100px', whiteSpace: 'nowrap' }}>Employee ID</TableCell>
                  <TableCell sx={{ width: '200px' }}>Name</TableCell>
                  <TableCell sx={{ width: '90px' }}>Gender</TableCell>
                  <TableCell sx={{ width: '90px' }}>Type</TableCell>
                  <TableCell sx={{ width: '90px' }}>Department</TableCell>
                  <TableCell sx={{ width: '100px' }}>Position</TableCell>
                  <TableCell sx={{ width: '120px', whiteSpace: 'nowrap' }}>Work Shift</TableCell>
                  <TableCell sx={{ width: '120px', whiteSpace: 'nowrap' }}>Face Enrollment</TableCell>
                  <TableCell sx={{ width: '90px' }}>Status</TableCell>
                  <TableCell align="right" sx={{ width: '130px' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <EmployeeTableSkeleton rows={pagination.rowsPerPage} />
                ) : paginatedEmployees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="textSecondary">
                        No employees found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedEmployees.map((emp) => (
                    <TableRow key={emp.id} hover>
                      <TableCell>
                        <Typography fontWeight="medium">{emp.employeeId}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: '#1976d2' }}>
                            {emp.firstName.charAt(0)}
                          </Avatar>
                          <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                            <Tooltip title={`${emp.firstName} ${emp.lastName}`} arrow placement="top">
                              <Typography
                                fontWeight="medium"
                                noWrap
                                sx={{
                                  maxWidth: { xs: 150, md: 200 },
                                  textOverflow: 'ellipsis',
                                  overflow: 'hidden',
                                  cursor: 'pointer'
                                }}
                              >
                                {emp.firstName} {emp.lastName}
                              </Typography>
                            </Tooltip>
                            <Tooltip title={emp.email} arrow placement="top">
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                noWrap
                                sx={{
                                  display: 'block',
                                  maxWidth: { xs: 150, md: 200 },
                                  textOverflow: 'ellipsis',
                                  overflow: 'hidden',
                                  cursor: 'pointer'
                                }}
                              >
                                {emp.email}
                              </Typography>
                            </Tooltip>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={emp.gender || 'N/A'}
                          size="small"
                          variant="outlined"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        <EmployeeTypeChip type={emp.employeeType} />
                      </TableCell>
                      <TableCell>
                        <Chip label={emp.department} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 120 }}>
                        <Tooltip title={emp.position} arrow placement="top">
                          <Typography variant="body2" noWrap sx={{ textOverflow: 'ellipsis', overflow: 'hidden', cursor: 'pointer' }}>
                            {emp.position}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ overflow: 'hidden' }}>
                        <Box sx={{ minWidth: 0, width: '100%' }}>
                          <Tooltip title={emp.workShift} arrow placement="top">
                            <Box sx={{ display: 'inline-block', maxWidth: '100%' }}>
                              <WorkShiftChip
                                shift={emp.workShift}
                                sx={{
                                  maxWidth: '100%',
                                  cursor: 'pointer',
                                  '& .MuiChip-label': {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    display: 'block'
                                  }
                                }}
                              />
                            </Box>
                          </Tooltip>
                          {emp.workHours && (
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              noWrap
                              sx={{
                                display: 'block',
                                mt: 0.5,
                                textOverflow: 'ellipsis',
                                overflow: 'hidden'
                              }}
                            >
                              {emp.workHours} hrs
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <EnrollmentChip enrolled={emp.faceEnrolled} />
                      </TableCell>
                      <TableCell>
                        <StatusChip status={emp.status} />
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewEmployee(emp.id)}
                            title="View"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditEmployee(emp)}
                            title="Edit"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          {!emp.faceEnrolled && (
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleEnrollFace(emp.id)}
                              title="Enroll Face"
                            >
                              <FaceIcon fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteEmployee(emp)}
                            title="Delete"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredEmployees.length}
            rowsPerPage={pagination.rowsPerPage}
            page={pagination.page}
            onPageChange={pagination.handleChangePage}
            onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          />
        </Paper >
      </Paper >
    </Box >
  )
}

export default Employees